import React, { useState, useEffect, use } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, RotateCcw } from 'lucide-react';
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import "../../../assets/css/colors.css";
import { useLanguage } from 'context/LanguageProvider';


const HashTableFunctionsGame = () => {
    const { darkMode } = useDarkMode();

    const selectedStage = JSON.parse(localStorage.getItem("selectedStage"));
    var games = selectedStage.games;
    var nameGameKey = localStorage.getItem("gameClicked");
    var selectedGame = null;
    for (let i = 0; i < games.length; i++) {
        if (games[i].game_title === nameGameKey) {
            selectedGame = games[i];
            break;
        }
    }

    const [hashTable, setHashTable] = useState({});
    const [currentChallenge, setCurrentChallenge] = useState(null);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [level, setLevel] = useState(1);
    const [lives, setLives] = useState(3);
    const [gameState, setGameState] = useState('playing');
    const [message, setMessage] = useState('');
    const [showExplanation, setShowExplanation] = useState(false);
    const [showGameOver, setShowGameOver] = useState(false);
    const [userInput, setUserInput] = useState("");
    const [showGameWon, setShowGameWon] = useState(false);
    const [correctAnswersCount, setCorrectAnswersCount] = useState(0);
    const { selLanguage, a, b } = useLanguage();

    const handlePlayAgain = () => {
        resetGame();
    };

    const generateHashTableData = (level) => {
        const tableSize = 7;
        const newTable = {};

        for (let i = 0; i < tableSize; i++) {
            newTable[i] = [];
        }

        const dataPool = [
            // Users
            { key: "user:anna", value: "Anna Papadopoulou" },
            { key: "user:nick", value: "Nikos Ioannou" },
            { key: "user:maria", value: "Maria Georgiou" },
            { key: "user:guest42", value: "Guest User" },

            // Sessions
            { key: "session:aF93kX", value: "active" },
            { key: "session:b71pLm", value: "expired" },
            { key: "session:mobile01", value: "active" },

            // Orders
            { key: "order#1023", value: "Pending" },
            { key: "order#1024", value: "Shipped" },
            { key: "order#1025", value: "Delivered" },

            // Cart / basket
            { key: "cart:user:anna", value: "3 items" },
            { key: "cart:user:nick", value: "1 item" },

            // Products
            { key: "product:BK-101", value: "Clean Code" },
            { key: "product:BK-202", value: "Algorithms in JS" },
            { key: "product:HD-550", value: "SSD 1TB" },

            // Settings / config
            { key: "config:theme", value: "dark" },
            { key: "config:language", value: "en" },
            { key: "feature:beta-search", value: "enabled" },

            // Tokens / security
            { key: "auth:token:anna", value: "valid" },
            { key: "auth:token:nick", value: "revoked" },
        ];

        // Περισσότερα στοιχεία με αύξηση επιπέδου (8-15 στοιχεία)
        const numItems = Math.min(8 + level, 20);
        const selectedData = dataPool.slice(0, numItems);

        selectedData.forEach(item => {
            const hash = item.key.length % tableSize;
            newTable[hash].push(item);
        });

        return newTable;
    };

    const hashFunction = (key) => {
        return key.length % 7; // Απλή hash function
    };

    useEffect(() => {
        const table = generateHashTableData(level);
        setHashTable(table);

        const allItems = Object.values(table).flat();
        const nonEmptyBuckets = Object.keys(table).filter(key => table[key].length > 0);

        const challengeTypes = [
            'get_key_exists',
            'get_key_not_exists',
            'contains_exists',
            'contains_not_exists',
            'size_total',
            'isEmpty_check',
            'keys_non_empty',
            'get_bucket_size',
            'put_operation',
            'remove_operation'
        ];

        const availableTypes = level <= 3 ? challengeTypes.slice(0, 6) : challengeTypes;
        const selectedType = availableTypes[Math.floor(Math.random() * availableTypes.length)];
        const challenge = generateSpecificChallenge(selectedType, table, allItems, nonEmptyBuckets);

        setCurrentChallenge(challenge);
        setMessage(
            selLanguage
                ? `Επίπεδο ${level}: Επιλέξτε τη σωστή απάντηση για τη συνάρτηση ${challenge.functionName}`
                : `Level ${level}: Choose the correct answer for function ${challenge.functionName}`
        );
    }, [level]);   // <-- κλειδί εδώ

    const generateSpecificChallenge = (type, table, allItems, nonEmptyBuckets) => {
        const totalSize = allItems.length;

        switch (type) {
            case 'get_key_exists':
                const existingItem = allItems[Math.floor(Math.random() * allItems.length)];
                const correctValue = existingItem.value;
                const hashIndex = hashFunction(existingItem.key);
                const bucketItems = table[hashIndex];

                return {
                    type: 'get',
                    functionName: 'get()',
                    correctAnswer: correctValue,
                    question: selLanguage ? `Γνωρίζουμε ότι το κλειδί "${existingItem.key}" είναι ήδη αποθηκευμένο στο hash table.Η hash function (key.length % 7) δίνει index ${hashIndex}, οπότε το κλειδί βρίσκεται στο bucket [${hashIndex}].Ποια τιμή θα επιστρέψει η κλήση get("${existingItem.key}") ;` : `We know that the key "${existingItem.key}" is already stored in the hash table. The hash function (key.length % 7) gives index ${hashIndex}, so the key is located in bucket [${hashIndex}]. What value will the call get("${existingItem.key}") return?`,
                    explanation: selLanguage ? `Το κλειδί "${existingItem.key}" υπάρχει στο bucket [${hashIndex}] με τιμή "${correctValue}".Η get() ψάχνει γραμμικά (linear search) μέσα στο συγκεκριμένο bucket και επιστρέφει την τιμή που είναι συνδεδεμένη με το κλειδί, άρα "${correctValue}".` : `The key "${existingItem.key}" exists in bucket [${hashIndex}] with value "${correctValue}". The get() function performs a linear search within that bucket and returns the value associated with the key, which is "${correctValue}".`
                };

            case 'get_key_not_exists':
                const fakeKey = "nonexistent" + Math.floor(Math.random() * 100);
                const fakeHashIndex = hashFunction(fakeKey);
                const fakeBucket = table[fakeHashIndex];

                return {
                    type: 'get',
                    functionName: 'get()',
                    correctAnswer: "null",
                    question: selLanguage ? `Το κλειδί "${fakeKey}" δεν έχει αποθηκευτεί ποτέ στο hash table.Η hash function (key.length % 7) δίνει index ${fakeHashIndex}, αλλά στο bucket [${fakeHashIndex}] υπάρχουν μόνο άλλα κλειδιά.Τι θα επιστρέψει η κλήση get("${fakeKey}") ;` : `The key "${fakeKey}" has never been stored in the hash table. The hash function (key.length % 7) gives index ${fakeHashIndex}, but the bucket [${fakeHashIndex}] contains only other keys. What will the call get("${fakeKey}") return?`,
                    explanation: selLanguage ? `Η get() ψάχνει στο bucket [${fakeHashIndex}], αλλά δεν βρίσκει κανένα στοιχείο με κλειδί "${fakeKey}".Όταν ένα κλειδί δεν υπάρχει στον πίνακα κατακερματισμού, η get() επιστρέφει null.` : `The get() function searches in bucket [${fakeHashIndex}], but it does not find any item with the key "${fakeKey}". When a key does not exist in the hash table, get() returns null.`,
                };

            case 'contains_exists':
                const existingKey = allItems[Math.floor(Math.random() * allItems.length)].key;
                const containsHashIndex = hashFunction(existingKey);
                const containsBucket = table[containsHashIndex];

                return {
                    type: 'contains',
                    functionName: 'contains()',
                    question: selLanguage ? `Στο bucket [${containsHashIndex}] υπάρχουν τα κλειδιά: ${containsBucket.map(i => i.key).join(', ')}.Θέλουμε να ελέγξουμε αν το κλειδί "${existingKey}" υπάρχει στο hash table.Τι θα επιστρέψει η κλήση contains("${existingKey}") ;` : `The bucket [${containsHashIndex}] contains the keys: ${containsBucket.map(i => i.key).join(', ')}. We want to check if the key "${existingKey}" exists in the hash table. What will the call contains("${existingKey}") return?`,
                    correctAnswer: "true",
                    explanation: selLanguage ? `Το κλειδί "${existingKey}" υπάρχει μέσα στο bucket [${containsHashIndex}].Η contains() ελέγχει αν κάποιο στοιχείο στο bucket έχει αυτό το κλειδί, οπότε το αποτέλεσμα είναι true.` : `The key "${existingKey}" exists within bucket [${containsHashIndex}]. The contains() function checks if any item in the bucket has that key, so the result is true.`
                };

            case 'contains_not_exists':
                const nonExistentKey = "missing" + Math.floor(Math.random() * 100);
                const missingHashIndex = hashFunction(nonExistentKey);
                const missingBucket = table[missingHashIndex];

                return {
                    type: 'contains',
                    functionName: 'contains()',
                    question: selLanguage ? `Το bucket [${missingHashIndex}] περιέχει ${missingBucket.length} στοιχεία, αλλά κανένα με κλειδί "${nonExistentKey}".Τι θα επιστρέψει η κλήση contains("${nonExistentKey}") ;` : `The bucket [${missingHashIndex}] contains ${missingBucket.length} items, but none with the key "${nonExistentKey}". What will the call contains("${nonExistentKey}") return?`,
                    correctAnswer: "false",
                    explanation: selLanguage ? `Η contains() θα ψάξει μέσα στο bucket [${missingHashIndex}], αλλά δεν θα βρει το κλειδί "${nonExistentKey}".Άρα θα επιστρέψει false, αφού το κλειδί δεν υπάρχει πουθενά στο hash table.` : `The contains() function will search within bucket [${missingHashIndex}], but it will not find the key "${nonExistentKey}". Therefore, it will return false, since the key does not exist anywhere in the hash table.`

                };

            case 'size_total':
                return {
                    type: 'size',
                    functionName: 'size()',
                    question: selLanguage ? `Το hash table αποτελείται από 7 buckets. Μερικά είναι άδεια, μερικά περιέχουν λίστα από ζεύγη <κλειδί, τιμή>.Συνολικά αυτή τη στιγμή υπάρχουν ${totalSize} ζεύγη μέσα σε ΟΛΑ τα buckets.Τι θα επιστρέψει η κλήση size() ;` : `The hash table consists of 7 buckets. Some are empty, some contain lists of <key, value> pairs. Currently, there are ${totalSize} pairs in ALL the buckets. What will the call size() return?`,
                    correctAnswer: totalSize.toString(),
                    explanation: selLanguage ? `Η size() μετράει πόσα ζεύγη <κλειδί, τιμή> υπάρχουν συνολικά σε όλα τα buckets του hash table.Αυτή τη στιγμή υπάρχουν ${totalSize} ζεύγη, άρα η size() επιστρέφει ${totalSize}.` : `The size() function counts how many <key, value> pairs exist in all the buckets of the hash table. Currently, there are ${totalSize} pairs, so size() returns ${totalSize}.`
                };

            case 'isEmpty_check':
                const isEmpty = allItems.length === 0;
                return {
                    type: 'isEmpty',
                    functionName: 'isEmpty()',
                    question: selLanguage ? `Αυτή τη στιγμή το hash table περιέχει συνολικά ${totalSize} στοιχεία.Τι θα επιστρέψει η κλήση isEmpty() ;` : `Currently, the hash table contains a total of ${totalSize} items. What will the call isEmpty() return?`,
                    correctAnswer: isEmpty ? "true" : "false",
                    explanation: selLanguage ? `Αν το hash table δεν έχει ΚΑΝΕΝΑ στοιχείο, τότε isEmpty() επιστρέφει true.Εδώ έχει ${totalSize} στοιχεί${totalSize === 1 ? "ο" : "α"}, άρα isEmpty() επιστρέφει ${isEmpty ? "true" : "false"}.` : `If the hash table has NO items, then isEmpty() returns true. Here it has ${totalSize} item${totalSize === 1 ? "" : "s"}, so isEmpty() returns ${isEmpty ? "true" : "false"}.`

                };

            case 'keys_non_empty':
                const keysWithData = nonEmptyBuckets.sort((a, b) => parseInt(a) - parseInt(b));
                const correctKeys = keysWithData.join(',');

                return {
                    type: 'keys',
                    functionName: 'keys()',
                    question: selLanguage ? `Σε ποια indices του πίνακα (0–6) υπάρχουν ΜΗ άδεια buckets;Γράψε τα indices σε αύξουσα σειρά, χωρισμένα με κόμμα (π.χ. 0,2,5).` : `At which indices of the array (0–6) are there NON-empty buckets? Write the indices in ascending order, separated by commas (e.g., 0,2,5).`,
                    correctAnswer: correctKeys,
                    explanation: selLanguage ? `Ελέγχουμε κάθε bucket από 0 μέχρι 6.Τα indices που έχουν τουλάχιστον ένα στοιχείο είναι: ${correctKeys}.Άρα η keys() επιστρέφει αυτά τα indices.` : `We check each bucket from 0 to 6. The indices that have at least one item are: ${correctKeys}. So keys() returns these indices.`
                };

            case 'get_bucket_size':
                const randomBucket = nonEmptyBuckets[Math.floor(Math.random() * nonEmptyBuckets.length)];
                const bucketSize = table[randomBucket].length;
                const bucketKeys = table[randomBucket].map(item => item.key).join(', ');

                return {
                    type: 'bucket',
                    functionName: 'bucket analysis',
                    question: selLanguage ? `Κοιτάμε μόνο το bucket [${randomBucket}].Εκεί βλέπουμε τα κλειδιά: ${bucketKeys}.Πόσα στοιχεία περιέχει αυτό το bucket;` : `We look only at bucket [${randomBucket}]. There we see the keys: ${bucketKeys}. How many items does this bucket contain?`,
                    correctAnswer: bucketSize.toString(),
                    explanation: selLanguage ? `Στο bucket [${randomBucket}] βλέπουμε ${bucketSize} στοιχεία.Άρα το μέγεθος της λίστας (bucket size) είναι ${bucketSize}.` : `In bucket [${randomBucket}] we see ${bucketSize} items. So the size of the list (bucket size) is ${bucketSize}.`
                };

            case 'put_operation':
                const newKey = "newKey" + Math.floor(Math.random() * 100);
                const expectedIndex = hashFunction(newKey);
                const currentBucketSize = table[expectedIndex].length;

                return {
                    type: 'put',
                    functionName: 'put()',
                    question: selLanguage ? `Το bucket [${expectedIndex}] έχει αυτή τη στιγμή ${currentBucketSize} στοιχεία.Θέλουμε να εισάγουμε ένα νέο ζεύγος με κλειδί "${newKey}" και τιμή "value" χρησιμοποιώντας put("${newKey}", "value").Η hash function είναι: key.length % 7.Σε ποιο index (bucket) θα τοποθετηθεί το νέο στοιχείο;` : `The bucket [${expectedIndex}] currently has ${currentBucketSize} items. We want to insert a new key-value pair with key "${newKey}" and value "value" using put("${newKey}", "value"). The hash function is: key.length % 7. At which index (bucket) will the new item be placed?`,
                    corrrectAnswer: expectedIndex.toString(),
                    explanation: selLanguage ? `Υπολογίζουμε το μήκος του κλειδιού "${newKey}" και εφαρμόζουμε τη hash function:${newKey.length} % 7 = ${expectedIndex}.Άρα το νέο στοιχείο τοποθετείται στο bucket [${expectedIndex}] και εκεί το bucket θα έχει πλέον ${currentBucketSize + 1} στοιχεία.` : `We calculate the length of the key "${newKey}" and apply the hash function: ${newKey.length} % 7 = ${expectedIndex}. So the new item is placed in bucket [${expectedIndex}] and the bucket will now have ${currentBucketSize + 1} items.`
                };

            case 'remove_operation':
                if (allItems.length > 0) {
                    const itemToRemove = allItems[Math.floor(Math.random() * allItems.length)];
                    const newSize = allItems.length - 1;
                    const removeHashIndex = hashFunction(itemToRemove.key);
                    const removeBucket = table[removeHashIndex];

                    return {
                        type: 'remove',
                        functionName: 'remove()',
                        question: selLanguage ? `Το hash table έχει αυτή τη στιγμή συνολικά ${allItems.length} στοιχεία.Στο bucket [${removeHashIndex}] υπάρχει το κλειδί "${itemToRemove.key}".Αν εκτελέσουμε remove("${itemToRemove.key}"), ποιο θα είναι το νέο αποτέλεσμα της size() ;` : `The hash table currently has a total of ${allItems.length} items. In bucket [${removeHashIndex}] there is the key "${itemToRemove.key}". If we execute remove("${itemToRemove.key}"), what will be the new result of size()?`,
                        correctAnswer: newSize.toString(),
                        explanation: selLanguage ? `Η remove("${itemToRemove.key}") αφαιρεί ένα μόνο ζεύγος <κλειδί, τιμή> από το bucket [${removeHashIndex}].Άρα το συνολικό πλήθος στοιχείων μειώνεται κατά 1: από ${allItems.length} γίνεται ${newSize}.Επομένως η size() θα επιστρέψει ${newSize}.` : `The remove("${itemToRemove.key}") removes only one key-value pair from bucket [${removeHashIndex}]. So the total number of items decreases by 1: from ${allItems.length} to ${newSize}. Therefore, size() will return ${newSize}.`
                    };
                }
                return generateSpecificChallenge('size_total', table, allItems, nonEmptyBuckets);

            default:
                return generateSpecificChallenge('get_key_exists', table, allItems, nonEmptyBuckets);
        }
    };

    const submitAnswer = () => {
        const userAnswer = userInput.trim().toLowerCase();
        const correctAnswer = currentChallenge.correctAnswer.toLowerCase();
        const isCorrect = userAnswer === correctAnswer;

        if (isCorrect) {
            setMessage(` Σωστή απάντηση!`);
            setGameState('correct');

            setTimeout(() => {
                if (level < 8) {
                    setLevel(prev => prev + 1);
                    setGameState('playing');
                    setSelectedAnswer(null);
                    setUserInput("");
                } else {
                    setMessage(selLanguage ? 'Συγχαρητήρια! Ολοκληρώσατε όλα τα επίπεδα!' : 'Congratulations! You have completed all levels!');
                    setShowGameWon(true);
                    setGameState('completed');
                }
            }, 1200);

            const newCount = correctAnswersCount + 1;
            setCorrectAnswersCount(newCount);
            if (newCount === 8) {
                setShowGameWon(true);
            }
        } else {
            const newLives = lives - 1;
            setLives(newLives);
            if (newLives === 0) {
                setShowGameOver(true);
            }

            setGameState('incorrect');
            setMessage(selLanguage ? ' Λάθος απάντηση.' : ' Wrong answer.');
            setShowExplanation(true);

            setTimeout(() => {
                //generateChallenge();
                setGameState('playing');
                setShowExplanation(false);
                setSelectedAnswer(null);
                setUserInput("");
            }, 2000);
        }
    };

    const resetGame = () => {
        setLevel(1);
        setLives(3);
        setGameState('playing');
        setShowExplanation(false);
        setSelectedAnswer(null);
        setShowGameOver(false)
        setShowGameWon(false);
        setCorrectAnswersCount(0);
    };



    const containerStyle = {
        minHeight: "90vh",
        background: darkMode
            ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)"
            : "var(--lightModeGameBackground)",
        padding: "30px 10px",
        borderRadius: "10px",

    };

    const pronStyle = {
        borderRadius: "20px",
        maxWidth: "800px",
        background: darkMode
            ? "linear-gradient(135deg, rgba(59, 130, 246, 0.4), rgba(29, 78, 216, 0.4))"
            : "linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(29, 78, 216, 0.2))",
        width: "90%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "15px 20px",
        margin: "0 auto 20px",
        color: darkMode ? "white" : "#1e293b"
    };

    return (
        <div classname="main-content" style={{
            minHeight: "100vh",
            background: darkMode
                ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)"
                : "var(--lightModeGameBackground)",
            padding: "20px 10px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
        }}>

            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={handlePlayAgain}

            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={handlePlayAgain}

            />
            <div className="max-w-6xl mx-auto">
                {/* Header με στατιστικά */}
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                    flexWrap: "wrap",
                    gap: "10px"
                }}>
                    <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>

                        <div style={{
                            color: darkMode ? "white" : "#1e293b",
                            padding: "12px 24px",
                            borderRadius: "12px",
                            fontWeight: "700",
                            fontSize: "18px",
                            border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #1e293b",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px"
                        }}>
                            Level: {level}/8
                        </div>

                    </div>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "row",
                        justifyContent: "right",
                        gap: "12px"
                    }}>

                        <div style={{
                            display: "flex",
                            gap: "6px",
                            alignItems: "center"
                        }}>
                            {[1, 2, 3].map((heartNum) => (
                                <div
                                    key={heartNum}
                                    style={{
                                        fontSize: "24px",
                                        filter: lives >= heartNum
                                            ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                            : "grayscale(100%) opacity(0.3)",
                                        transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                        transition: "all 0.3s ease",
                                        animation: lives >= heartNum ? "heartbeat 2s ease-in-out infinite" : "none"
                                    }}
                                >
                                    ❤️
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Περιγραφή παιχνιδιού */}
                <div style={pronStyle}>
                    {selLanguage ? "Σε αυτό το παιχνίδι θα εξασκηθείς στις βασικές συναρτήσεις ενός hash table.Σε κάθε επίπεδο βλέπεις τον τρέχοντα πίνακα κατακερματισμού και μια ερώτηση που περιγράφει μια λειτουργία πάνω του.Αν απαντήσεις σωστά, στην κάθε ερώτηση προχωράς στο επόμενο επίπεδο. Αν κάνεις λάθος, χάνεις μία ζωή – όταν οι ζωές σου φτάσουν στο μηδέν, το παιχνίδι τελειώνει.Ο στόχος σου είναι να ολοκληρώσεις και τα 8 επίπεδα" : "In this game, you will practice the basic functions of a hash table. In each level, you see the current hash table and a question describing an operation on it. If you answer correctly, you move to the next level. If you answer incorrectly, you lose a life – when your lives reach zero, the game ends. Your goal is to complete all 8 levels."}
                </div>

                {/* Hash Table Visualization */}
                <motion.div

                    style={{
                        background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                        borderRadius: "15px",
                        padding: "25px",
                        marginBottom: "25px",
                        width: "100%",
                        border: "2px solid rgba(255,255,255,0.2)"
                    }}
                >
                    <h3 style={{
                        color: "white",
                        textAlign: "center",
                        marginBottom: "20px",
                        fontSize: "24px",
                        fontWeight: "bold"
                    }}>
                        HashTable
                    </h3>

                    <div style={{
                        display: "grid",
                        gap: "12px",
                        height: "450px",
                        overflowY: "auto",
                    }}>
                        {Object.keys(hashTable).sort((a, b) => parseInt(a) - parseInt(b)).map(index => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.3, delay: parseInt(index) * 0.05 }}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    background: "rgba(255,255,255,0.1)",
                                    borderRadius: "12px",
                                    padding: "15px",
                                    border: "1px solid rgba(255,255,255,0.2)"
                                }}
                            >
                                <div style={{
                                    width: "50px",
                                    height: "50px",
                                    background: "linear-gradient(135deg, #1655bbff 0%, #042277ff 100%)",
                                    borderRadius: "10px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "white",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    marginRight: "15px"
                                }}>
                                    [{index}]
                                </div>

                                <div style={{ flex: 1 }}>
                                    {!hashTable[index] || hashTable[index].length === 0 ? (
                                        <div style={{
                                            color: "#cbd5e1",
                                            fontStyle: "italic",
                                            fontSize: "14px"
                                        }}>
                                            Empty
                                        </div>
                                    ) : (
                                        <div style={{
                                            display: "flex",
                                            gap: "8px",
                                            flexWrap: "wrap",
                                            alignItems: "center"
                                        }}>
                                            {hashTable[index].map((item, itemIndex) => (
                                                <motion.div
                                                    key={`${item.key}-${itemIndex}`}
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    transition={{ duration: 0.2, delay: itemIndex * 0.05 }}
                                                    style={{
                                                        padding: "6px 12px",
                                                        borderRadius: "12px",
                                                        background: "#e2e2e2ff",
                                                        color: "rgba(8, 56, 134, 1)",
                                                        fontWeight: "bold",
                                                        fontSize: "12px",
                                                        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                                        border: "2px solid #2f31a3ff"
                                                    }}
                                                >
                                                    {item.key}: "{item.value}"
                                                </motion.div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                <AnimatePresence>
                    {gameState !== 'playing' && message && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            style={{
                                marginTop: "20px",
                                padding: "15px 20px",
                                borderRadius: "10px",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                color: darkMode ? "white" : "#1e293b",
                                gap: "10px",
                                background:
                                    gameState === "correct"
                                        ? "rgba(16, 185, 129, 0.15)"
                                        : "rgba(239, 68, 68, 0.15)",
                                border:
                                    gameState === "correct"
                                        ? "1px solid rgba(16, 185, 129, 0.6)"
                                        : "1px solid rgba(239, 68, 68, 0.6)",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                }}
                            >

                                <span>{message}</span>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Challenge Section */}
                {currentChallenge && gameState === 'playing' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{
                            background: "none",
                            borderRadius: "15px",
                            padding: "25px",
                            marginBottom: "20px",
                            border: "2px solid rgba(255,255,255,0.2)"
                        }}
                    >
                        <h3 style={{
                            color: darkMode ? "white" : "#1e293b",
                            fontSize: "20px",
                            marginBottom: "15px",
                            fontWeight: "bold"
                        }}>
                            {selLanguage ? `Συνάρτηση ${currentChallenge.functionName}:` : `Function ${currentChallenge.functionName}:`}
                        </h3>

                        <p style={{
                            color: darkMode ? "white" : "#1e293b",
                            fontSize: "18px",
                            lineHeight: "1.6"
                        }}>
                            {currentChallenge.question}
                        </p>

                        {/* Multiple Choice Options */}
                        <div style={{
                            display: "grid",
                            gap: "12px",
                            marginBottom: "20px"
                        }}>
                            <input
                                type="text"
                                placeholder="Πληκτρολογήστε την απάντηση σας..."
                                value={userInput}
                                onChange={(e) => setUserInput(e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "15px 20px",
                                    borderRadius: "10px",
                                    border: "2px solid rgba(255,255,255,0.3)",
                                    background: "rgba(255,255,255,0.1)",
                                    color: "white",
                                    fontSize: "16px",
                                    outline: "none"
                                }}
                            />
                        </div>

                        <button
                            onClick={submitAnswer}
                            style={{
                                padding: "15px 30px",
                                borderRadius: "10px",
                                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                color: "white",
                                border: "none",
                                cursor: "pointer",
                                fontWeight: "bold",
                                fontSize: "16px",
                                width: "100%"
                            }}
                        >
                            {selLanguage ? "Υποβολή Απάντησης" : "Submit Answer"}
                        </button>
                    </motion.div>
                )}
            </div>
        </div >
    );
};

export default HashTableFunctionsGame;